import {Component, Input, Optional, ViewChild} from '@angular/core';
import {BSValidators} from "../shared/bsvalidators";
import {columns, stickyColumns} from "./coldefs-client";
import {Client, ClientStatus} from "../interfaces/client";
import {SubscribedComponent} from "../subscribed.component";
import {ActivatedRoute, NavigationCancel, NavigationEnd, Router} from "@angular/router";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ClientService} from "../shared/client.service";
import {Globals} from "../shared/Globals";
import {AuthService} from "../shared/auth.service";
import {User, UserStatus} from "../interfaces/user";
import {TableType} from "../interfaces/paged";
import {TimelineType} from "../charts/timeline-chart/timeline-chart.component";
import {SettingsService} from "../shared/settings.service";
import {TranslateService} from "@ngx-translate/core";
import {TitleService} from "../shared/title.service";
import {NbDialogRef} from "@nebular/theme";
import {InvoicingService} from "../shared/invoicing.service";
import {COMPANY_TABS, COMPANY_TAB_ICONS, CompanyTabsService, companyTabAllowed, groupMissing} from "./company-tab/company-tabs";
import {ClientCrmSectionComponent} from "./client-crm-section/client-crm-section.component";

@Component({
    selector: 'bst-client',
    templateUrl: './client.component.html',
    styleUrls: ['./clients.component.scss'],
    standalone: false
})
export class ClientComponent extends SubscribedComponent{

  @ViewChild('timeline', {static: false}) timeline;
  @ViewChild('crm') crm?: ClientCrmSectionComponent;
  @Input() public showTimeline: boolean = true;

  id: number;
  client: Client = null;
  me: User;
  columns = columns;
  headers = stickyColumns;
  ClientStatus = ClientStatus;
  editing: boolean = false;
  form: FormGroup;
  Globals = Globals;
  couldArchive: boolean = true;
  couldEdit: boolean = true;
  TableType = TableType;

  // Route-tabset config: Overview (default child) + CVs (sub-route).
  // Tabs are computed in ngOnInit once we know whether we're on /clients/:id
  // or /company so the routes point at the right parent.
  public tabs: any[] = [];

  // True when mounted imperatively inside a dialog (preview/create popup)
  // rather than via the router. The route-tabset/router-outlet only make sense
  // in routed mode; in a popup the orphan primary outlet re-activates the root
  // route and mounts a second ClientComponent (the double-overlay bug). Popups
  // hand in data imperatively (PopupDialogComponent Object.assigns id/client onto
  // the instance before our ngOnInit): preview popups set id, create popups set
  // client+editing; the routed path (/clients/:id, /company) sets neither.
  public embedded: boolean = false;

  // Set once in ngOnInit: ngOnDestroy runs after the router has already moved
  // on to the next URL, so `onCompany` there would read the wrong route.
  private routedCompany = false;
  private destroyed = false;

  constructor(private route: ActivatedRoute,
              private clientService: ClientService,
              private titleService: TitleService,
              private router: Router,
              public auth: AuthService,
              private settings: SettingsService,
              private fb: FormBuilder,
              private translate: TranslateService,
              @Optional() private dialogRef: NbDialogRef<any>,
              private companyTabs: CompanyTabsService,
              private invoicing: InvoicingService) {
    super();
  }

  public get onCompany(): boolean {
    return !this.embedded && this.router.url.startsWith('/company');
  }

  // A My company settings tab (Billing, VAT, ...): the shell's own Profile body and buttons step aside.
  public get isOnCompanySettingsTab(): boolean {
    const url = this.router.url.split(/[?#]/)[0];
    return this.onCompany && url.startsWith('/company/') && url !== '/company/profile';
  }

  private buildTabs(): void {
    if (this.onCompany) {
      // Titles carry the missing count: nb-route-tabset renders the title as text only.
      const groups = groupMissing(this.companyTabs.missing$.getValue() ?? []);
      this.tabs = COMPANY_TABS.filter(t => companyTabAllowed(t, this.auth)).map(t => {
        const title = this.translate.instant('companytabs.' + t);
        const count = groups[t].length;
        return {
          title: count ? this.translate.instant('companytabs.count', {tab: title, count}) : title,
          route: `/company/${t}`,
          icon: COMPANY_TAB_ICONS[t],
          responsive: false,
        };
      });
      return;
    }

    const basePath = `/clients/${this.id}`;
    this.tabs = [
      {
        title: this.translate.instant('client.tab.overview'),
        route: basePath,
        icon: 'home-outline',
        responsive: true,
      },
    ];

    // External clients (/clients/:id) still expose the CVs tab; listForClient
    // 403s for staffers so admin-only gate the surface, with exact=false to
    // keep the tab highlighted while on /cvs/studio.
    if (this.auth.isAdmin) {
      this.tabs.push({
        title: this.translate.instant('client.tab.cvs'),
        route: `${basePath}/cvs`,
        icon: 'file-text-outline',
        responsive: true,
        activeLinkOptions: { exact: false },
      });
    }
  }

  /**
   * True when the current URL is a CVs-related sub-route (CV list or
   * Templates manager). Used by the template to hide the client-overview body
   * so those sub-pages have the page to themselves.
   */
  public get isOnCvsRoute(): boolean {
    const url = this.router.url.split('?')[0];
    return url.endsWith('/cvs')
      || url.includes('/cvs/studio')
      || url.includes('/cv/studio')
      || url.includes('/cv/templates');
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Capture popup-vs-routed mode synchronously, before async route work sets id (see `embedded`).
    this.embedded = this.id != null || this.client != null;
    this.routedCompany = this.onCompany;

    if (this.routedCompany) {
      this.companyTabs.reset();
      this.companyTabs.shellDirty = () => this.isDirty;
      this.subs.push(this.companyTabs.missing$.subscribe(() => this.client && this.buildTabs()));
      if (companyTabAllowed('billing', this.auth)) {
        // One read so the counts show on every tab; the Billing editors refresh it on load and save.
        // M2: a slice may have already published its own, more current list by the time this
        // resolves; a late shell push must not overwrite it with the pre-save record.
        // A read that outlives this shell must not fill in the next shell's reset state either.
        this.invoicing.getCompany().then(c => {
          if (!this.destroyed && this.companyTabs.missing$.getValue() === null) { this.companyTabs.missing$.next(c.missing ?? []); }
        }).catch(() => null);
      }
    }

    // Edit mode lives on the Overview body, but the parent ClientComponent
    // stays mounted across child-route navigation. Without this guard the
    // edit <input> can persist into /cvs sub-routes where the Save/Cancel
    // controls are hidden, leaving the user with no way to commit or cancel.
    this.subs.push(this.router.events.subscribe(evt => {
      // The tab guard has already asked about unsaved edits; close edit mode with the body it belongs to.
      if (evt instanceof NavigationEnd && this.editing && (this.isOnCvsRoute || this.isOnCompanySettingsTab)) {
        this.editing = false;
      }
      if (evt instanceof NavigationEnd && this.onCompany) {
        // Below 600px the strip scrolls; keep the open tab, and its count, in view.
        setTimeout(() => document.querySelector('nb-route-tabset.company-tabs .route-tab.active')
          ?.scrollIntoView({block: 'nearest', inline: 'nearest'}));
      }
      // A focus queued by a readiness link must not fire later on some other visit.
      if (evt instanceof NavigationCancel) {
        this.companyTabs.focus$.next(null);
      }
    }));

    this.me = this.auth.user.getValue();

    const controls = {};
    for(let c of this.columns.concat(this.headers)){
      const validators = c.validators || [];
      controls[c.name] = [{value: null, disabled: (c.disabled || false)}, validators];
    }

    controls['document_language'] = [{value: 'nl', disabled: !this.auth.isAdmin}];
    this.form = this.fb.group(controls);
    this.subs.push(BSValidators.armPhoneValidator(this.form.get('phone')));

    if(this.id){
      this.mapClient(this.id);
      this.buildTabs();
    }
    else if(!this.client){
      this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = parseInt(id);
          await this.mapClient(this.id);
        }
        else{
          await this.mapClient(null);
          // could be just a new client
          // this.client = this.clientService.emptyClient;
          // this.editClient();
        }

        const edit = this.route.snapshot.queryParamMap.get('edit');
        if(this.client && this.client.status !== ClientStatus.ARCHIVED && edit == "true" && this.couldEditMyself){
          this.editClient();
        }
        this.buildTabs();

        await this.titleService.newTitle(this.myclient ? 'My Company' : null);
      }));
    }
    else if(this.couldEditMyself && this.editing){
      this.editClient();
    }
  }

  get isDirty(): boolean {
    return this.editing && this.form.dirty && this.form.touched;
  }

  ngOnDestroy() {
    // M1: reset() after unsubscribing, not before -- otherwise the still-live missing$
    // subscription rebuilds tabs from reset's null during teardown.
    super.ngOnDestroy();
    this.destroyed = true;
    if (this.routedCompany) { this.companyTabs.reset(); }
  }

  get couldEditMyself(): boolean{
    return !this.myclient || (this.myclient && this.auth.isAdmin);
  }

  editClient(){
    const value = {
      name: this.client.name,
      description: this.client.description,
      matching_description: this.client.matching_description,
      url: this.client.url,
      phone: this.client.phone,
      email: this.client.email,
      address: this.client.address,
      document_language: this.client.document_language || 'nl',
      project: []
    };

    this.form.setValue(value);

    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.editing = true;
  }

  public get myclient(){
    return this.me?.client && this.me.client.id === this.client.id;
  }

  public get link(): string{
    const register = '/register';
    if(this.myclient && this.client.domain && window.location){
      // return window.location.protocol + '//' + window.location.hostname + register + '/' + this.client.domain;
      if(window.location.hostname.indexOf(this.client.domain) !== -1){
        return window.location.protocol + '//' + window.location.hostname + register;
      }
      else{
        return window.location.protocol + '//' + (this.client.domain + '.') + window.location.hostname + register;
      }
    }
    return null;
  }

  async mapClient(id: number){
    this.loading.next(true);
    if(id) {
      // Toasted by errorify; a missing client falls through to the redirect below.
      this.client = await this.clientService.getClient(id).catch(() => null);
    }
    else if(this.me && this.me.client){
      await this.mapClient(this.me.client.id);
    }

    if(!this.client){
      await this.router.navigate(['clients']);
    }

    this.loading.next(false);

  }

  private get values(): Client{
    // const client = {...this.client, ...this.form.getRawValue()};
    const client = this.form.getRawValue();
    if (client.phone && (<any>client.phone).e164Number) {
      client.phone = (<any>client.phone).e164Number;
    }
    return client;
  }

  async saveClient(){
    if(!this.loading.getValue()){
      this.loading.next(true);
      try {
        if(!this.client.id){
          this.client = await this.clientService.addClient(this.values);
          if(this.embedded && this.dialogRef){
            this.dialogRef.close(this);
          }
        }
        else {
          this.client = await this.clientService.saveClient(this.client.id, this.values, 'Saved!');
          if(this.myclient){
            await this.auth.bootstrap();
          }
        }
        this.touched = true;
        this.editing = false;
      } finally {
        this.loading.next(false);
      }
    }
  }

  async setStatus(status: ClientStatus) {
    if(!this.loading.getValue() && this.client.id) {
      this.loading.next(true);
      try {
        // Archive/unarchive (ACTIVE<->ARCHIVED) go through the generic
        // update endpoint (PUT /clients/:id) via setStatus().
        this.client = await this.clientService.setStatus(this.client.id, status);
        this.editing = false;
      } finally {
        this.loading.next(false);
      }
    }
  }

  /** A prospect is a Client in one of the prospect lifecycle states (2/3/4). */
  get isProspect(): boolean {
    return [ClientStatus.PROSPECT, ClientStatus.PROSPECT_ON_HOLD, ClientStatus.PROSPECT_LOST]
      .includes(this.client?.status);
  }

  /**
   * Archived->Prospect is a CRM feature: the backend 404s the transition
   * when the flag is off, so the button must stay hidden with it.
   */
  get crmEnabled(): boolean {
    return !!this.auth.user.getValue()?.crm_enabled;
  }

  /**
   * The CRM panel matches the CRM nav gate: feature flag on AND a staffer or
   * admin. With CRM dark its contacts/activity requests would just 404, and
   * consultants would see edit affordances they cannot use.
   *
   * Visible for any non-archived Client, not just prospects: winning a deal
   * promotes a Client out of the prospect statuses, and the panel (contacts,
   * activity timeline, opportunity history) is exactly as relevant to an
   * active client relationship as to a prospect one, gating it to
   * prospect-only made it disappear right when a win made it matter most.
   *
   * Kept off embedded previews (project/timeline-chart/table-object client
   * popups, several of which pass couldEdit: false): those callers didn't
   * ask for a full CRM section with add/edit/delete affordances, and the
   * three extra list calls (contacts/activities/opportunities) aren't worth
   * paying for a preview popup nobody's looking at CRM data in.
   *
   * Kept off myclient (the tenant's own org, "My company"): contacts, an
   * activity log and an opportunity pipeline describe a relationship with an
   * external company, which the tenant's own org isn't. Mirrors how
   * client-organization-section already excludes is_tenant_root via
   * `linkable` (#1343).
   */
  get showCrmPanel(): boolean {
    return !this.embedded
      && !this.myclient
      && this.client?.status !== ClientStatus.ARCHIVED
      && !!this.auth.user.getValue()?.crm_enabled
      && (this.auth.isAdmin || this.auth.isStaffer);
  }

  /**
   * Promote this prospect to an active Client via the generic update endpoint
   * (PUT /clients/:id). The backend only permits legal transitions.
   */
  async promote(): Promise<void> {
    if (this.loading.getValue() || !this.client?.id) { return; }
    this.loading.next(true);
    try {
      this.client = await this.clientService.setStatus(this.client.id, ClientStatus.ACTIVE);
      this.editing = false;
    } finally {
      this.loading.next(false);
    }
  }

  /** Re-fetch the current client from the server. */
  async reload(): Promise<void> {
    if (this.client?.id) {
      await this.mapClient(this.client.id);
      // A merge moves contacts and opportunities here without changing clientId, so the panel would not reload itself.
      await this.crm?.loadCrm();
    }
  }

  get type(){
    return <TimelineType>this.settings.userSetting('timeline_type', TimelineType.Day);
  }

  // Same gate as the server (#1515): an active Admin of this tenant, with trips on.
  get canSeeReimbursementRates(): boolean {
    const me = this.auth.user.getValue();
    return this.myclient && this.auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE && !!me?.trips_enabled;
  }

  // Same gate as the server (#1516): an active Admin of this tenant, with invoicing on.
  get canSeeInvoicing(): boolean {
    const me = this.auth.user.getValue();
    return this.auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE && !!me?.invoicing_enabled;
  }

  // A deep link from an invoice issue (?field=town). Not on embedded previews/popups (see
  // showCrmPanel above): they didn't navigate here via that link.
  get focusField(): string | null {
    return this.embedded ? null : this.route.snapshot.queryParamMap.get('field');
  }

  /** Intake address and forwarding help. Admin only; staffers see the address on the Intake page. */
  get canSeeIntake(): boolean {
    const me = this.auth.user.getValue();
    return this.auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE && !!me?.intake_enabled;
  }

  /**
   * Whether CV theme admin is available on this client page. Scoped to an
   * admin viewing their own client. Theme admin renders inside the Edit
   * mode block; the CVs tab itself is always available on a client the
   * caller can access.
   */
  get canSeeCvSection(): boolean {
    return !!this.client?.id && this.myclient && this.auth.isAdmin;
  }

}
